import { MembershipProfileComplementaryItem } from '@models/membership/MembershipProfileComplementaryItem'
import { membershipProfileComplementaryItemSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createMembershipProfileComplementaryItem as createMembershipProfileComplementaryItemAPI,
  deleteMembershipProfileComplementaryItem as deleteMembershipProfileComplementaryItemAPI,
  fetchMembershipProfileComplementaryItems,
  updateMembershipProfileComplementaryItem as updateMembershipProfileComplementaryItemAPI,
} from '@services/membership/MembershipProfileComplementaryItemService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<MembershipProfileComplementaryItem> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'membershipProfileComplementaryItem',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getMembershipProfileComplementaryItemsSuccess,
  setQuery: setMembershipProfileComplementaryItemQuery,
  reset: resetMembershipProfileComplementaryItemState,
  resetQueryAndIds: resetMembershipProfileComplementaryItemQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: MembershipProfileComplementaryItem }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMembershipProfileComplementaryItems = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedMembershipProfileComplementaryItem = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectMembershipProfileComplementaryItemById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectMembershipProfileComplementaryItemsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedMembershipProfileComplementaryItem =
  (complementaryItem: MembershipProfileComplementaryItem | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<MembershipProfileComplementaryItem>(complementaryItem)
      )
    )

export const getMembershipProfileComplementaryItems = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchMembershipProfileComplementaryItems(params),
    [membershipProfileComplementaryItemSchema],
    slice.actions
  )

export const createMembershipProfileComplementaryItem = (
  complementaryItem: MembershipProfileComplementaryItem
) =>
  SBAPI.CreateDispatched(
    createMembershipProfileComplementaryItemAPI(complementaryItem),
    membershipProfileComplementaryItemSchema,
    slice.actions
  )

export const updateMembershipProfileComplementaryItem = (
  complementaryItem: MembershipProfileComplementaryItem
) =>
  SBAPI.UpdateDispatched<MembershipProfileComplementaryItem>(
    updateMembershipProfileComplementaryItemAPI(complementaryItem),
    complementaryItem,
    slice.actions
  )

export const deleteMembershipProfileComplementaryItem = (
  complementaryItem: MembershipProfileComplementaryItem
) =>
  SBAPI.DestroyDispatched<MembershipProfileComplementaryItem>(
    deleteMembershipProfileComplementaryItemAPI(complementaryItem),
    complementaryItem,
    slice.actions
  )
